import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setActiveTool, setPointDrawActive, openRightDrawerTab } from '../../features/ui/uiSlice';
import {
  setDrawActive as setEquipmentDrawActive,
  setDrawMode as setEquipmentDrawMode,
} from '../../features/equipment/equipmentSlice';
import {
  loadGeneratedRouteThunk,
  LLM_ROUTE_TAG,
} from '../../features/layout/loadGeneratedRouteThunk';
import { beginEndpointCapture } from '../../features/layout/routeEndpointCapture';
import { routeEquipmentIds } from '../../features/layout/routeMarkBaseline';
import { appendChatMessage, setChatBusy, setChatOpen, useChatSession } from './chatSessionStore';
import { layoutElementFromEquipmentInstance } from '../../utils/stockerExport';
import {
  sendChatMessage,
  generateRouteStream,
  importDxfTrack,
  ChatMessage,
} from '../../utils/dxfApi';
import { LayoutElement } from '../../types';
import { snapshotLayoutSummary } from '../../features/assistant/snapshotLayoutSummary';
import { ChatMessageContent } from './ChatMessageContent';

/**
 * Floating fab-planner assistant. The user types plain sentences ("create routes for all the
 * blocks", "connect 677 and 680"); the backend interprets the intent and returns a reply plus an
 * action the widget runs — load an updated route, or ask the user to mark blocks then generate.
 */
const AssistantChatWidget: React.FC = () => {
  const dispatch = useAppDispatch();
  const equipmentInstances = useAppSelector((s) => s.equipment.instances);
  const layoutElements = useAppSelector((s) => s.layout.elements);
  const layers = useAppSelector((s) => s.layers.layers);
  const layerOrder = useAppSelector((s) => s.layers.layerOrder);
  const floorPlanId = useAppSelector((s) => s.floorplan.floorPlanId);
  const floorTransform = useAppSelector((s) => s.floorplan.transform);
  const toolboxSettingsOpen = useAppSelector((s) => s.ui.toolboxSettingsOpen);

  // Conversation + open/busy live in chatSessionStore so they survive the widget unmounting on
  // a 2D <-> 3D switch (and keep receiving a running generation's progress meanwhile).
  const { messages, open, busy } = useChatSession(floorPlanId);
  const setOpen = setChatOpen;
  const setBusy = setChatBusy;
  const [input, setInput] = useState('');
  const [marking, setMarking] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  const push = (role: ChatMessage['role'], content: string) =>
    appendChatMessage(floorPlanId, { role, content });

  // Build a fab-project canvas from a set of equipment-instance ids (defaults to all marked areas).
  const buildCanvas = (ids: string[] = Object.keys(equipmentInstances)) => {
    const elements: Record<string, LayoutElement> = {};
    const order: string[] = [];
    for (const id of ids) {
      const el = layoutElementFromEquipmentInstance(equipmentInstances[id]);
      if (el) {
        elements[el.id] = el;
        order.push(el.id);
      }
    }
    return { kind: 'fab-project', elements, elementOrder: order, layers, layerOrder };
  };

  // Current generated track as a route dict, so "connect" can modify the existing route.
  const buildRoute = () => {
    const track = Object.values(layoutElements).filter(
      (e) => e.customProperties?.generatedBy === LLM_ROUTE_TAG
    );
    if (track.length === 0) return null;
    const elements: Record<string, LayoutElement> = {};
    const order: string[] = [];
    for (const e of track) {
      elements[e.id] = e;
      order.push(e.id);
    }
    return {
      version: '1.0.0',
      elements,
      elementOrder: order,
      routePlan: { connections: [], stops: [] },
    };
  };

  const enterMarking = () => {
    setMarking(true);
    dispatch(setActiveTool('select'));
    dispatch(setPointDrawActive(false));
    dispatch(setEquipmentDrawMode('rectangle'));
    dispatch(setEquipmentDrawActive(true));
    dispatch(openRightDrawerTab('equipment'));
  };

  const finishMarkingAndGenerate = async () => {
    setMarking(false);
    dispatch(setEquipmentDrawActive(false));
    // Every area the user identified by hand — marked now, before, or after an earlier run.
    const newIds = routeEquipmentIds(equipmentInstances);
    if (newIds.length === 0) {
      push(
        'assistant',
        'I didn’t find any equipment you marked — mark at least one block, then click Generate.'
      );
      return;
    }
    // Step 2: capture the route START and END points on the canvas (Esc to skip).
    push(
      'assistant',
      'Now click the START point on the canvas, then the END point. (Esc to skip.)'
    );
    let endpoints: { start: { x: number; y: number }; end: { x: number; y: number } } | undefined;
    try {
      endpoints = await beginEndpointCapture(dispatch);
      push('assistant', 'Got the start and end points.');
    } catch {
      endpoints = undefined;
      push('assistant', 'Skipped start/end — generating without fixed endpoints.');
    }
    setBusy(true);
    push(
      'assistant',
      `Generating routes and stockers from ${newIds.length} marked block(s)…`
    );
    try {
      // Stream so the LLM plan's retry attempts show live in the chat.
      await generateRouteStream({
        canvas: buildCanvas(newIds),
        startPoint: endpoints?.start ?? null,
        endPoint: endpoints?.end ?? null,
        onEvent: (ev) => {
          switch (ev.type) {
            case 'status':
              if (ev.message) push('assistant', ev.message);
              break;
            case 'attempt_failed':
              push(
                'assistant',
                `Attempt ${ev.n} of ${ev.total} failed${ev.reason ? ` (${ev.reason})` : ''} — retrying…`
              );
              break;
            case 'attempts_exhausted':
              push(
                'assistant',
                `The planner didn’t return a usable plan after ${ev.total} tries — falling back to an automatic layout.`
              );
              break;
            case 'done': {
              const n = dispatch(loadGeneratedRouteThunk(ev.route));
              push(
                'assistant',
                `${ev.message ?? 'Route generated.'}${n ? ` Loaded ${n} track elements.` : ''}`
              );
              break;
            }
            case 'error':
              push('assistant', `Route generation failed: ${ev.message ?? 'unknown error'}`);
              break;
            default:
              break;
          }
        },
      });
    } catch (err) {
      push(
        'assistant',
        `Route generation failed: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setBusy(false);
    }
  };

  const runExtractDxfTrack = async (payloadLayers?: unknown) => {
    if (!floorPlanId) {
      push('assistant', 'Open a floor plan (DXF) first, then I can rebuild its existing track.');
      return;
    }
    if (!floorTransform || !(floorTransform.invScale > 0)) {
      push('assistant', 'The floor plan geometry isn’t ready yet — reopen it and try again.');
      return;
    }
    const layers = Array.isArray(payloadLayers)
      ? (payloadLayers as unknown[]).map(String).filter(Boolean)
      : undefined;
    try {
      const resp = await importDxfTrack({ floorPlanId, layers, transform: floorTransform });
      const n = dispatch(loadGeneratedRouteThunk(resp.route));
      push(
        'assistant',
        resp.message ?? (n ? `Loaded ${n} track elements.` : 'No track was found.')
      );
    } catch (err) {
      push(
        'assistant',
        `Track extraction failed: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    push('user', text);
    setBusy(true);
    try {
      const history = messages.slice(-8);
      const resp = await sendChatMessage({
        message: text,
        history,
        canvas: buildCanvas(),
        route: buildRoute(),
        layoutSummary: snapshotLayoutSummary(),
      });
      push('assistant', resp.reply);
      if (resp.action?.type === 'load_route') {
        const n = dispatch(loadGeneratedRouteThunk(resp.action.payload?.route));
        if (n === 0) push('assistant', '(No track elements were returned to load.)');
      } else if (resp.action?.type === 'request_block_selection') {
        enterMarking();
      } else if (resp.action?.type === 'extract_dxf_track') {
        await runExtractDxfTrack(resp.action.payload?.layers);
      }
    } catch (err) {
      push(
        'assistant',
        `Sorry, something went wrong: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const bubble = useMemo(
    () => (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700"
        title="Fab assistant"
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>
    ),
    []
  );

  if (toolboxSettingsOpen) return null;

  if (!open) return bubble;

  return (
    <div className="fixed bottom-5 right-5 z-40 flex h-[32rem] w-[24rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-blue-600 px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2l2.4 7.4H22l-6 4.5 2.3 7.1-6.3-4.6L7.7 21l2.3-7.1-6-4.5h7.6z" />
            </svg>
          </span>
          <span className="text-sm font-semibold">Fab Assistant</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded p-1 hover:bg-white/20"
          title="Close"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-gray-50 px-4 py-3">
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={
                (m.role === 'user'
                  ? 'bg-blue-600 text-white max-w-[85%]'
                  : 'bg-white text-gray-800 border border-gray-200 max-w-[95%]') +
                ' rounded-2xl px-3 py-2 text-sm shadow-sm'
              }
            >
              <ChatMessageContent
                content={m.content}
                tone={m.role === 'user' ? 'user' : 'assistant'}
              />
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-400 shadow-sm">
              thinking…
            </div>
          </div>
        )}
      </div>

      {/* Marking action bar */}
      {marking && (
        <div className="flex items-center justify-between gap-2 border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          <span>Mark the blocks on the canvas, then…</span>
          <button
            type="button"
            onClick={() => void finishMarkingAndGenerate()}
            disabled={busy}
            className="rounded-lg bg-amber-500 px-3 py-1.5 font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
          >
            Generate
          </button>
        </div>
      )}

      {/* Input */}
      <div className="flex items-end gap-2 border-t border-gray-100 bg-white px-3 py-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Ask me to route or connect blocks…"
          className="max-h-24 flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || !input.trim()}
          className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
          title="Send"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m22 2-7 20-4-9-9-4Z" />
            <path d="M22 2 11 13" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AssistantChatWidget;
